'use strict';

import React from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { studyBuilderGradientTopSx } from './StudyBuilderSectionTitle.jsx';
import { selectionsSummaryLines } from '../studyBuilderSummaryLabels.js';

export function StudySummaryCard({
	fitSelections,
	primaryProduct,
	sampleInfo,
	estimatedCostDisplay,
	estimatedCompletionDisplay,
	totalSampleCount,
	quoteSampleBasisDisplay,
	/** When false, omits the estimated cost row (e.g. before the user reveals the quote). */
	showEstimatedCost = true,
}) {
	const selectionRows = selectionsSummaryLines(fitSelections, primaryProduct);

	const sampleCountDisplay = totalSampleCount != null ? String(totalSampleCount) : sampleInfo.numberOfSamples;

	return (
		<Paper
			data-testid="study-summary-card"
			elevation={0}
			sx={{
				p: { xs: 2.5, md: 3 },
				border: `1px solid ${Brand.precisionGrey}`,
				borderRadius: 3,
				position: { md: 'sticky' },
				top: { md: 24 },
				background: Brand.pureWhite,
				boxShadow: '0 8px 32px rgba(0, 29, 56, 0.08)',
				overflow: 'hidden',
				'&::before': studyBuilderGradientTopSx,
			}}
		>
			<Box sx={{ pt: 0.5 }}>
				<Typography variant="overline" sx={{ fontWeight: 800, color: Brand.molecularBlue, letterSpacing: 1.4, fontSize: '0.72rem' }}>
					Study summary
				</Typography>
				<Divider sx={{ my: 2, borderColor: Brand.precisionGrey }} />

				<Typography variant="caption" sx={{ fontWeight: 800, color: Brand.darkModeBlue, letterSpacing: 0.8, textTransform: 'uppercase' }}>
					Selections
				</Typography>
				<Box sx={{ mt: 1.25, mb: 2.5 }}>
					{selectionRows.map(([lbl, val], i) => (
						<SummaryRow
							key={lbl}
							label={lbl}
							value={val}
							emphasis={i === 0 || i === selectionRows.length - 1}
						/>
					))}
				</Box>

				<Typography variant="caption" sx={{ fontWeight: 800, color: Brand.darkModeBlue, letterSpacing: 0.8, textTransform: 'uppercase' }}>
					Sample logistics
				</Typography>
				<Box sx={{ mt: 1.25 }}>
					<SummaryRow label="Number of samples" value={sampleCountDisplay} testId="summary-sample-count" />
					{quoteSampleBasisDisplay ? (
						<SummaryRow label="Quote sample basis" value={quoteSampleBasisDisplay} testId="summary-quote-sample-basis" />
					) : null}
					<SummaryRow label="Sample volume" value={sampleInfo.sampleVolume} />
					<SummaryRow label="Sample shipping" value={sampleInfo.sampleShipping} />
					<SummaryRow label="Planned shipping date" value={sampleInfo.plannedShippingDate || '—'} />
					<SummaryRow label="Estimated completion date" value={estimatedCompletionDisplay || '—'} />
					{showEstimatedCost ? (
						<SummaryRow label="Estimated cost" value={estimatedCostDisplay || '—'} emphasis testId="summary-estimated-cost" />
					) : null}
				</Box>
			</Box>
		</Paper>
	);
}

function SummaryRow({ label: lbl, value, emphasis, testId }) {
	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				gap: 2,
				py: 0.65,
				borderBottom: `1px solid rgba(211, 223, 231, 0.6)`,
				'&:last-of-type': { borderBottom: 'none' },
			}}
			data-testid={testId}
		>
			<Typography variant="body2" sx={{ color: Brand.molecularBlue, flexShrink: 0 }}>
				{lbl}
			</Typography>
			<Typography
				variant="body2"
				sx={{
					fontWeight: emphasis ? 700 : 500,
					textAlign: 'right',
					maxWidth: '58%',
					color: emphasis ? Brand.darkModeBlue : 'inherit',
				}}
			>
				{value ?? '—'}
			</Typography>
		</Box>
	);
}
